import { useEffect, useMemo } from 'react'
import { useStoreContext } from 'src/contexts/store-context'
import { useCart } from 'src/sdk/cart/useCart'
import { PageTypes } from 'src/typings/pageTypes'
import { getDynamicYieldContext } from 'src/utils/getDynamicYieldContext'

export const DYNAMIC_YIELD_CONTEXT_READY_EVENT = 'dynamic-yield:context-ready'

type Props = {
  pathname?: string
  productSku?: string | null
  categoryPath?: string[]
  pageType?: PageTypes
}

const DynamicYieldPageContext = ({
  pathname = '/',
  productSku = null,
  categoryPath = [],
  pageType = PageTypes.NotSet,
}: Props) => {
  const { items } = useCart()
  const { favouriteStore } = useStoreContext()
  const cartSkus = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .map((item) => item?.itemOffered?.sku)
            .filter((sku): sku is string => Boolean(sku))
        )
      ),
    [items]
  )
  const branchId = useMemo(
    () => favouriteStore?.id?.trim() || null,
    [favouriteStore?.id]
  )

  // Componente senza UI: viene montato nei layout Gatsby dal contenitore browser.
  // Scrive il contesto pagina consumato dagli script DY dopo il primo montaggio
  // lato client.
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Gira sul client per ogni contenitore pagina Gatsby renderizzato. Aggiorna
    // il contesto usato da DY per scegliere le campagne, ma non scarica campagne
    // da solo: sono gli script DY gia' caricati a consumare questo oggetto
    // globale.
    const safePathname = window.location?.pathname ?? pathname

    const recommendationContext = getDynamicYieldContext({
      pathname: safePathname,
      productSku,
      cartSkus,
      categoryPath,
      pageType,
      branchId,
    })

    // Gli script pubblici DY leggono questo globale. Tenerlo aggiornato e'
    // importante nei cambi rotta lato client, dove Gatsby non ricarica il
    // documento.
    window.DY = window.DY ?? {}
    window.DY.recommendationContext = recommendationContext
    window.dispatchEvent(new CustomEvent(DYNAMIC_YIELD_CONTEXT_READY_EVENT))
  }, [pathname, productSku, cartSkus, categoryPath, pageType, branchId])

  return null
}

export default DynamicYieldPageContext
